import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { compose, flatten } from 'ramda';
import { useTranslation } from 'react-i18next';
import { StandardModal, ModalContent } from 'pureUi/Modal';
import BookingGuestInformationForm from 'pureUi/BookingGuestInformationForm';
import * as TableCardUI from 'pureUi/TableCard';
import PureCheckbox from 'pureUi/Checkbox';
import Checkbox from 'ui/Checkbox';

import { DisplayTotalsBreakdown } from 'components';
import { formatPrice, filterByObjectProperties } from 'utils';

import connect from './SummaryFormExtras.state';
import * as SummaryFormExtrasStyles from './SummaryFormExtras.styles';

import { HotelName } from '../SummaryForm/SummaryForm.styles';
import { noop } from 'utils';

import { IFine } from 'store/modules/bookingManager/sagas/bookingTransforms/BookingModel';
import * as BookingBuilderSelectors from 'store/modules/bookingBuilder';

import * as AuthSelectors from 'store/modules/auth/selectors';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import * as UISelectors from 'store/modules/ui/selectors';
import { trackCartClick } from 'integrations/trackingEvents';
import * as BookingBuilderCustomItemSelectors from 'store/modules/bookingBuilder/subdomains/customItem/selectors';
import InfoIcon from 'ui/Icons/components/Info.component';
import { SvgIcon } from 'ui/SvgIcon';
import { theme } from '../../../tailwind.config';
import ArrowDown from 'ui/Icons/arrowDown.component.svg';
import ArrowUp from 'ui/Icons/arrowUp.component.svg';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';
import { BookingMargin, useMarginChange } from './BookingMargin';
import { TravelAgentInfo } from './TravelAgentInfo';
import { RadioButtons } from 'components/RadioButtons/RadioButtons';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

export const renderHotelName = ({ name }) => {
  return (
    <TableCardUI.TableCardBox>
      <TableCardUI.TableCardRow $depth={1}>
        <HotelName>{name}</HotelName>
      </TableCardUI.TableCardRow>
    </TableCardUI.TableCardBox>
  );
};

const InfoIconWithModal = ({ infoTitle, infoDescription }: { infoTitle: string; infoDescription: React.ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMobile } = useResponsive();

  return (
    <React.Fragment>
      <button
        onClick={e => {
          e.preventDefault();
          setIsModalOpen(true);
        }}
        className="outline-none bg-transparent border-none cursor-pointer flex justify-center items-center flex-shrink-0"
      >
        <SvgIcon
          IconComponent={InfoIcon}
          defaultFill={theme.colors['brown-100']}
          hoverFill={theme.colors['teal-100']}
          activeFill={theme.colors['teal-100']}
          width="24"
          height="24"
        />
      </button>

      {isMobile ? (
        <MobileBottomDrawer isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={infoTitle}>
          <p className="text-left m-0">{infoDescription}</p>
        </MobileBottomDrawer>
      ) : (
        isModalOpen && (
          <StandardModal onClose={() => setIsModalOpen(false)}>
            <ModalContent>
              <h2 className="uppercase color-gold">{infoTitle}</h2>
              <p>{infoDescription}</p>
            </ModalContent>
          </StandardModal>
        )
      )}
    </React.Fragment>
  );
};

const formatInlinePriceText = (currencyCode, total, isOnRequestOrPartiallyOnRequest, convert?: (value: number) => number): string => {
  if (isOnRequestOrPartiallyOnRequest === true) return 'Price Available On Request';
  const convertedTotal = convert ? convert(total) : total;
  return `${currencyCode}${formatPrice(convertedTotal)}`;
};

const renderInlinePrice = (translate, currencyCode, total, totalBeforeDiscount, isOnRequestOrPartiallyOnRequest, convert?: (value: number) => number) => {
  if (isOnRequestOrPartiallyOnRequest === true) {
    return <label>{translate('labels.priceAvailableOnRequest')}</label>;
  }
  const convertedTotal = convert ? convert(total) : total;
  const convertedTotalBeforeDiscount = convert ? convert(totalBeforeDiscount) : totalBeforeDiscount;
  if (total !== totalBeforeDiscount) {
    if (totalBeforeDiscount === '0.00') {
      return (
        <React.Fragment>
          <SummaryFormExtrasStyles.InlinePriceLabel
            data-discounted={true}
            margin-right={true}
            className="price-after-discount"
          >
            {currencyCode}
            {formatPrice(convertedTotal)}
          </SummaryFormExtrasStyles.InlinePriceLabel>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <SummaryFormExtrasStyles.InlinePriceLabel
          data-discounted={true}
          margin-right={true}
          className="price-after-discount"
        >
          {currencyCode}
          {formatPrice(convertedTotal)}
        </SummaryFormExtrasStyles.InlinePriceLabel>
        <SummaryFormExtrasStyles.InlinePriceLabel data-secondary={true} className="price-before-discount">
          {currencyCode}
          {formatPrice(convertedTotalBeforeDiscount)}
        </SummaryFormExtrasStyles.InlinePriceLabel>
      </React.Fragment>
    );
  } else {
    return (
      <label className="price">
        {currencyCode}
        {formatPrice(convertedTotal)}
      </label>
    );
  }
};

const renderTransferOptionsSimple = (
  translate,
  selectedTransferOptions,
  transferOptions,
  updateTransferAction,
  hotelUuid,
  currencyCode,
  convert?: (value: number) => number
) => {
  const simpleTransfers = transferOptions.map(t => ({
    name: t.products[0].name,
    uuid: t.products[0].uuid,
    direction: t.meta && t.meta.direction && t.meta.direction ? t.meta.direction : undefined,
    description: t.products[0].meta.description,
    priceFormatted: renderInlinePrice(
      translate,
      currencyCode,
      t.total,
      t.totalBeforeDiscount,
      t.isOnRequestOrPartiallyOnRequest,
      convert
    ),
  }));

  const handleRadioClick = (value: string, label: React.ReactNode) => {
    const dynamicParameters = ParameterService.getParameters();
    // OWA-5182
    if (dynamicParameters.WOOPRA_DOMAIN) {
      // @ts-ignore
      window.woopra &&
        // @ts-ignore
        window.woopra.track('cart-click', {
          type: 'transfers',
          hotelUuid,
          itemLabel: label,
          itemUuid: value,
        });
    }
    const selectedTransfer = transferOptions.find(t => t.products[0].uuid === value);
    const itemLabel = selectedTransfer
      ? `${selectedTransfer.products[0].name} ${formatInlinePriceText(
          currencyCode,
          selectedTransfer.total,
          selectedTransfer.isOnRequestOrPartiallyOnRequest,
          convert
        )}`
      : String(label ?? '');

    trackCartClick({ type: 'transfers', hotelUuid, itemLabel, itemUuid: value });
    updateTransferAction(
      {
        uuid: value,
        direction: undefined,
      },
      hotelUuid
    );
  };

  const handleCheckboxClick = to => {
    const dynamicParameters = ParameterService.getParameters();
    // OWA-5182
    if (dynamicParameters.WOOPRA_DOMAIN) {
      // @ts-ignore
      window.woopra &&
        // @ts-ignore
        window.woopra.track('cart-click', {
          type: 'transfers',
          hotelUuid,
          itemLabel: `${to.name} ${to.priceFormatted}`,
          itemUuid: to.uuid,
        });
    }
    const selectedTransfer = transferOptions.find(t => t.products[0].uuid === to.uuid);
    const priceText = selectedTransfer
      ? formatInlinePriceText(
          currencyCode,
          selectedTransfer.total,
          selectedTransfer.isOnRequestOrPartiallyOnRequest,
          convert
        )
      : '';
    trackCartClick({ type: 'transfers', hotelUuid, itemLabel: `${to.name} ${priceText}`, itemUuid: to.uuid });
    updateTransferAction(
      {
        uuid: to.uuid,
        direction: to.direction,
      },
      hotelUuid
    );
  };

  const bothWayTransfersOptions = simpleTransfers
    .filter(t => t.direction === undefined)
    .map(t => {
      return {
        value: t.uuid,
        label: (
          <SummaryFormExtrasStyles.ProductLabel className="normal-case">
            <span className="return-transfer-option">
              {t.name} {t.priceFormatted}{' '}
            </span>
            <InfoIconWithModal
              infoTitle={`${t.name} ${t.priceFormatted}`}
              infoDescription={t.description}
            />
          </SummaryFormExtrasStyles.ProductLabel>
        ),
      };
    });

  const selectedBothWays = (bothWayTransfersOptions || []).find(x => x.value === selectedTransferOptions[0]?.uuid);

  const bothWayTransferMarkup = (
    <div className="transfers-return-options">
      <RadioButtons
        id="transfer"
        options={bothWayTransfersOptions}
        onChange={handleRadioClick}
        containerClasses="flex flex-col w-full"
        labelClasses="w-full justify-between items-center"
        selectedOption={selectedBothWays?.value}
      />
    </div>
  );

  const inDirectionTransferOptions = simpleTransfers.filter(t => t.direction === 'in');
  const outDirectionTransferOptions = simpleTransfers.filter(t => t.direction === 'out');

  const buildMarkupForOneWayTransfers = transfers => {
    return (
      <SummaryFormExtrasStyles.OptionList>
        {transfers.map(to => {
          const isChecked = selectedTransferOptions.some(sto => sto.uuid === to.uuid && sto.direction === to.direction);
          return (
            <li key={to.name}>
              <label className="optionListLabel transfer-item">
                <PureCheckbox onChange={() => handleCheckboxClick(to)} checked={isChecked} className="checkbox" />
                <SummaryFormExtrasStyles.ProductLabel className="normal-case">
                  <span className="labelText">
                    {to.name} {to.priceFormatted}{' '}
                  </span>
                  <InfoIconWithModal
                    infoTitle={`${to.name} ${to.priceFormatted}`}
                    infoDescription={to.description}
                  />
                </SummaryFormExtrasStyles.ProductLabel>
              </label>
            </li>
          );
        })}
      </SummaryFormExtrasStyles.OptionList>
    );
  };

  const inDirectionTransfersMarkup = buildMarkupForOneWayTransfers(inDirectionTransferOptions);
  const outDirectionTransfersMarkup = buildMarkupForOneWayTransfers(outDirectionTransferOptions);

  return (
    <div>
      {bothWayTransfersOptions && (
        <div>
          <label className="uppercase font-bold return-transfers-label">Return Transfers</label>
          {bothWayTransferMarkup}
        </div>
      )}

      {bothWayTransfersOptions && inDirectionTransferOptions && <hr />}

      {inDirectionTransferOptions && (
        <React.Fragment>
          <label className="uppercase font-bold one-way-transfers-in-label">One Way Transfers (To Hotel)</label>
          <div className="transfers-in-options">{inDirectionTransfersMarkup}</div>
        </React.Fragment>
      )}

      {((bothWayTransfersOptions && outDirectionTransferOptions) ||
        (inDirectionTransferOptions && outDirectionTransferOptions)) && <hr />}
      {outDirectionTransfersMarkup && (
        <React.Fragment>
          <label className="uppercase font-bold one-way-transfers-out-label">One Way Transfers (From Hotel)</label>
          <div className="transfers-out-options">{outDirectionTransfersMarkup}</div>
        </React.Fragment>
      )}
    </div>
  );
};

const renderGroundServices = (
  translate,
  currencyCode,
  selectedGroundServices,
  groundServices,
  updateGroundServiceAction,
  hotelUuid,
  convert?: (value: number) => number
) => {
  // OWA-5182
  const handleCheckboxClick = (gsProduct, hotelUuid) => {
    const dynamicParameters = ParameterService.getParameters();
    if (dynamicParameters.WOOPRA_DOMAIN) {
      // @ts-ignore
      window.woopra &&
        // @ts-ignore
        window.woopra.track('cart-click', {
          type: 'ground-services',
          hotelUuid,
          itemLabel: gsProduct.name,
          itemUuid: gsProduct.uuid,
        });
    }
    trackCartClick({ type: 'ground-services', hotelUuid, itemLabel: gsProduct.name, itemUuid: gsProduct.uuid });
    updateGroundServiceAction(gsProduct, hotelUuid);
  };
  return (
    <div className="ground-services-options">
      {groundServices.map(gs => {
        const gsProduct = gs.products[0];
        const isChecked = selectedGroundServices.some(sgs => sgs.uuid === gsProduct.uuid);

        return (
          <label
            className="flex items-center cursor-pointer font-hurmegeometric-sans py-3 w-full"
            key={`${gsProduct.uuid}/${gsProduct.name}`}
          >
            <Checkbox
              onChange={() => handleCheckboxClick(gsProduct, hotelUuid)}
              checked={isChecked}
              wrapperClassName="mr-3"
              checkedClassName="-top-2px"
              color="brown-prime"
              borderColor="brown-prime"
              borderWidth={2}
              left={'2px'}
              rounded
              large
            />
            <SummaryFormExtrasStyles.ProductLabel className="normal-case">
              <span>
                {gsProduct.name}{' '}
                {renderInlinePrice(
                  translate,
                  currencyCode,
                  gs.total,
                  gs.totalBeforeDiscount,
                  gs.isOnRequestOrPartiallyOnRequest,
                  convert
                )}
              </span>
              <InfoIconWithModal
                infoTitle={`${gsProduct.name} ${formatInlinePriceText(currencyCode, gs.total, gs.isOnRequestOrPartiallyOnRequest, convert)}`}
                infoDescription={gsProduct.meta.description}
              />
            </SummaryFormExtrasStyles.ProductLabel>
          </label>
        );
      })}
    </div>
  );
};

const isCustomItem = supplementProduct =>
  supplementProduct.options && supplementProduct.options.genericIdentifier === 'customItem';

const renderAddons = (
  translate,
  currencySymbol,
  selectedSupplements,
  selectedFines,
  availableSupplements: IFine[],
  availableFines,
  updateSupplementAction,
  updateFineAction,
  hotelUuid,
  customItem,
  convert?: (value: number) => number
) => {
  const renderSupplement = (sp, idx, isCustomSupplement) => {
    const supplementProduct = sp.products[0];
    const isChecked = selectedSupplements.some(sgs => sgs.uuid === supplementProduct.uuid);

    const handleSupplementAction = (supplementProduct, hotelUuid) => {
      const dynamicParameters = ParameterService.getParameters();
      // OWA-5182
      if (dynamicParameters.WOOPRA_DOMAIN) {
        // @ts-ignore
        window.woopra &&
          // @ts-ignore
          window.woopra.track('cart-click', {
            type: 'other-items',
            hotelUuid,
            itemLabel: `${supplementProduct.name}`,
            itemUuid: supplementProduct.uuid,
          });
      }
      trackCartClick({ type: 'other-items', hotelUuid, itemLabel: `${supplementProduct.name}`, itemUuid: supplementProduct.uuid });
      updateSupplementAction(supplementProduct, hotelUuid);
    };

    return (
      <label
        className="flex items-center cursor-pointer w-full font-hurmegeometric-sans py-3"
        key={`${supplementProduct.uuid}/${supplementProduct.name}`}
      >
        <Checkbox
          onChange={() =>
            isCustomSupplement
              ? customItem.actions.remove(idx, hotelUuid)
              : handleSupplementAction(supplementProduct, hotelUuid)
          }
          checked={isChecked || isCustomSupplement}
          disabled={sp.mandatory && !isCustomSupplement}
          wrapperClassName="mr-3"
          checkedClassName="-top-2px"
          color="brown-prime"
          borderColor="brown-prime"
          borderWidth={2}
          left={'2px'}
          rounded
          large
        />
        <SummaryFormExtrasStyles.ProductLabel className="normal-case">
          <span>
            {supplementProduct.name}{' '}
            {renderInlinePrice(
              translate,
              currencySymbol,
              sp.total,
              sp.totalBeforeDiscount,
              sp.isOnRequestOrPartiallyOnRequest,
              convert
            )}
          </span>
          <InfoIconWithModal
            infoTitle={`${supplementProduct.name} ${formatInlinePriceText(currencySymbol, sp.total, sp.isOnRequestOrPartiallyOnRequest, convert)}`}
            infoDescription={supplementProduct.meta.description}
          />
        </SummaryFormExtrasStyles.ProductLabel>
      </label>
    );
  };

  const standardSupplements = availableSupplements.filter(sp => !isCustomItem(sp.products[0]));
  const customSupplements = availableSupplements.filter(sp => isCustomItem(sp.products[0]));

  const fineMarkup = (
    <React.Fragment>
      {availableFines.map(fp => {
        const fineProduct = fp.products[0];
        const isChecked = selectedFines.some(sgs => sgs.uuid === fineProduct.uuid);
        return (
          <label
            className="flex items-center cursor-pointer w-full font-hurmegeometric-sans py-3"
            key={`${fineProduct.uuid}/${fineProduct.name}`}
          >
            <Checkbox
              onChange={() => {
                trackCartClick({
                  type: 'other-items',
                  hotelUuid,
                  itemLabel: fineProduct.name,
                  itemUuid: fineProduct.uuid,
                });
                updateFineAction(fineProduct, hotelUuid);
              }}
              checked={isChecked}
              wrapperClassName="mr-3"
              checkedClassName="-top-2px"
              color="brown-prime"
              borderColor="brown-prime"
              borderWidth={2}
              left={'2px'}
              rounded
              large
            />
            <SummaryFormExtrasStyles.ProductLabel>
              <span>
                {fineProduct.name}{' '}
                {renderInlinePrice(
                  translate,
                  currencySymbol,
                  fp.total,
                  fp.totalBeforeDiscount,
                  fp.isOnRequestOrPartiallyOnRequest,
                  convert
                )}
              </span>
              <InfoIconWithModal
                infoTitle={`${fineProduct.name} ${formatInlinePriceText(currencySymbol, fp.total, fp.isOnRequestOrPartiallyOnRequest, convert)}`}
                infoDescription={fineProduct.meta.description}
              />
            </SummaryFormExtrasStyles.ProductLabel>
          </label>
        );
      })}
    </React.Fragment>
  );

  return (
    <div className="addon-options">
      {standardSupplements.map((sp, idx) => renderSupplement(sp, idx, false))}
      {fineMarkup}
      {customSupplements.map((sp, idx) => renderSupplement(sp, idx, true))}
    </div>
  );
};

const renderGuestInfo = (data, onChange, validation) => {
  return (
    <TableCardUI.TableCardBox className="mt-4 mb-4">
      <TableCardUI.TableCardRow $depth={3}>
        <SummaryFormExtrasStyles.Title>Lead Guest Info</SummaryFormExtrasStyles.Title>
        <BookingGuestInformationForm
          bookingGuestFormValues={data}
          onValueChange={onChange}
          sections={{ guestInfo: true }}
          validation={validation}
        />
      </TableCardUI.TableCardRow>
    </TableCardUI.TableCardBox>
  );
};

export const SummaryFormExtras = ({
  booking = {} as any,
  currencySymbol,
  id,
  setIsBookingSummarySectionCollapsed,
  updateTransferAction,
  updateGroundServiceAction,
  updateSupplementAction,
  updateFineAction,
  customItemActions,
  updateBookingGuestInformationAction,
  onChange = noop,
  total = 0,
}) => {
  const { t } = useTranslation();
  const { dynamicParameters } = useDynamicParameters();
  const { convert, displayCurrencySymbol, isConverting } = useCurrencyConversion();
  const effectiveCurrencyCode = isConverting ? displayCurrencySymbol : currencySymbol;

  const availableAddons = useSelector(BookingBuilderSelectors.bookingAvailableAddonsSelector);
  const groundServices = useSelector(BookingBuilderSelectors.bookingAvailableGroundServicesSelector);
  const transfers = useSelector(BookingBuilderSelectors.bookingAvailableTransfersSelector);
  const availableFines = useSelector(BookingBuilderSelectors.bookingAvailableFinesSelector);
  const availableSupplements = useSelector(BookingBuilderSelectors.bookingAvailableSupplementsSelector);
  const selectedFines = useSelector(BookingBuilderSelectors.bookingRequestedFinesSelector);
  const selectedGroundServices = useSelector(BookingBuilderSelectors.bookingRequestedGroundServicesSelector);
  const selectedSupplements = useSelector(BookingBuilderSelectors.bookingRequestedSupplementsSelector);
  const selectedTransfers = useSelector(BookingBuilderSelectors.bookingRequestedTransfersSelector);
  const canBook = useSelector(BookingBuilderSelectors.bookingCanBookSelector);
  const guestInfo = useSelector(BookingBuilderSelectors.guestInfoSelector);
  const grandTotal = useSelector(BookingBuilderSelectors.bookingBuilderTotalSelector);
  const travelAgent = useSelector(AgentsSelectors.selectedTaSelector);
  const isTransferSectionCollapsed = useSelector(UISelectors.getIsTransferSectionCollapsed);
  const isGroundServicesSectionCollapsed = useSelector(UISelectors.getIsGroundServicesSectionCollapsed);
  const isAddonsSectionCollapsed = useSelector(UISelectors.getIsAddonsSectionCollapsed);
  const selectedTransfersBreakdown = useSelector(BookingBuilderSelectors.bookingRequestedTransfersBreakdownSelector);
  const isTAMarginApplied = useSelector(BookingBuilderSelectors.isTAMarginAppliedSelector);
  const taMarginType = useSelector(BookingBuilderSelectors.taMarginTypeSelector);
  const taMarginAmount = useSelector(BookingBuilderSelectors.taMarginAmountSelector);
  const customItem = {
    payload: useSelector(BookingBuilderCustomItemSelectors.customItemPayloadSelector),
    validation: useSelector(BookingBuilderCustomItemSelectors.customItemValidationSelector),
  };
  const isPristine = useSelector(BookingBuilderSelectors.isPristineSelector);
  const domainValidation = useSelector(BookingBuilderSelectors.domainValidationSelector);

  const isSr: boolean = useSelector(AuthSelectors.isInternalUserOrActingAsInternalUser);
  const isRl: boolean = useSelector(AuthSelectors.isRlOrActingAsRl);

  useEffect(() => {
    updateBookingGuestInformationAction({
      guestTitle: null,
      guestFirstName: '',
      guestLastName: '',
    });
  }, []);

  const validation = isPristine ? {} : domainValidation;
  const hasTAInfo = isSr && !isRl;

  const { onMarginChange } = useMarginChange(id);

  const selectedGroundServicesBreakdown = useCallback(() => {
    const selectedGroundServiceProducts = filterByObjectProperties(
      flatten(groundServices.map(g => g.products)),
      selectedGroundServices,
      ['uuid']
    );

    if (selectedGroundServiceProducts.length >= 1) {
      return selectedGroundServiceProducts.map(stp => stp.name).join(' & ');
    }

    return 'None selected';
  }, [selectedGroundServices, groundServices]);

  const selectedAddonsBreakdown = useCallback(() => {
    const selectedAddons = flatten([selectedFines, selectedSupplements]);

    const customItemProducts = flatten(availableSupplements.map(sp => sp.products)).filter(isCustomItem);

    const selectedAddonProducts = filterByObjectProperties(
      flatten(availableAddons.map(a => a.products)),
      selectedAddons,
      ['uuid']
    ).concat(customItemProducts);

    if (selectedAddonProducts.length >= 1) {
      return selectedAddonProducts.map(stp => stp.name).join(' & ');
    }

    return 'None selected';
  }, [selectedFines, selectedSupplements, availableAddons]);

  const renderTransfersWrapper = () => {
    return (
      <TableCardUI.TableCardBox className="table-card-box">
        <TableCardUI.TableCardRow className="table-card-row" $depth={1}>
          {/* @ts-ignore */}
          <HotelName>{t('labels.transfers')}</HotelName>
        </TableCardUI.TableCardRow>

        <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
          <SummaryFormExtrasStyles.CollapseTitle>
            <label
              className={`selected-transfers-label uppercase font-bold ${
                !isTransferSectionCollapsed ? 'color-teal' : ''
              }`}
            >
              {selectedTransfersBreakdown}
            </label>
            <SummaryFormExtrasStyles.CollapseToggle
              className="collapse-toggle transfers"
              type="button"
              onClick={() =>
                setIsBookingSummarySectionCollapsed({
                  type: 'transfers',
                  value: !isTransferSectionCollapsed,
                })
              }
            >
              {isTransferSectionCollapsed === false ? (
                <SvgIcon width="13" height="19" IconComponent={ArrowUp} defaultFill={theme.colors.black} />
              ) : (
                <SvgIcon width="13" height="19" IconComponent={ArrowDown} defaultFill={theme.colors.black} />
              )}
            </SummaryFormExtrasStyles.CollapseToggle>
          </SummaryFormExtrasStyles.CollapseTitle>
        </TableCardUI.TableCardRow>

        {isTransferSectionCollapsed === false && (
          <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
            {renderTransferOptionsSimple(t, selectedTransfers, transfers, updateTransferAction, id, effectiveCurrencyCode, isConverting ? convert : undefined)}
          </TableCardUI.TableCardRow>
        )}
      </TableCardUI.TableCardBox>
    );
  };

  const renderGroundServicesWrapper = () => {
    const breakdown = selectedGroundServicesBreakdown();
    return (
      <TableCardUI.TableCardBox className="table-card-box mt-4">
        <TableCardUI.TableCardRow className="table-card-row" $depth={1}>
          {/* @ts-ignore */}
          <HotelName>{t('labels.groundServices')}</HotelName>
        </TableCardUI.TableCardRow>
        <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
          <SummaryFormExtrasStyles.CollapseTitle>
            <label className={`uppercase font-bold ${!isGroundServicesSectionCollapsed ? 'color-teal ' : ''}`}>
              {breakdown}
            </label>

            <SummaryFormExtrasStyles.CollapseToggle
              className="collapse-toggle ground-services"
              type="button"
              onClick={() =>
                setIsBookingSummarySectionCollapsed({
                  type: 'ground_services',
                  value: !isGroundServicesSectionCollapsed,
                })
              }
            >
              {isGroundServicesSectionCollapsed === false ? (
                <SvgIcon width="13" height="19" IconComponent={ArrowUp} defaultFill={theme.colors.black} />
              ) : (
                <SvgIcon width="13" height="19" IconComponent={ArrowDown} defaultFill={theme.colors.black} />
              )}
            </SummaryFormExtrasStyles.CollapseToggle>
          </SummaryFormExtrasStyles.CollapseTitle>
        </TableCardUI.TableCardRow>
        {isGroundServicesSectionCollapsed === false && (
          <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
            {renderGroundServices(
              t,
              effectiveCurrencyCode,
              selectedGroundServices,
              groundServices,
              updateGroundServiceAction,
              id,
              isConverting ? convert : undefined
            )}
          </TableCardUI.TableCardRow>
        )}
      </TableCardUI.TableCardBox>
    );
  };

  const renderAddonsWrapper = () => {
    const breakdown = selectedAddonsBreakdown();
    return (
      <TableCardUI.TableCardBox className="table-card-box mt-4">
        <TableCardUI.TableCardRow className="table-card-row" $depth={1}>
          {/* @ts-ignore */}
          <HotelName>{t('labels.addons')}</HotelName>
        </TableCardUI.TableCardRow>

        <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
          <SummaryFormExtrasStyles.CollapseTitle>
            <label className={`uppercase font-bold ${!isAddonsSectionCollapsed ? 'color-teal ' : ''}`}>
              {breakdown}
            </label>
            <SummaryFormExtrasStyles.CollapseToggle
              className="collapse-toggle addons"
              type="button"
              onClick={() =>
                setIsBookingSummarySectionCollapsed({
                  type: 'addons',
                  value: !isAddonsSectionCollapsed,
                })
              }
            >
              {isAddonsSectionCollapsed === false ? (
                <SvgIcon width="13" height="19" IconComponent={ArrowUp} defaultFill={theme.colors.black} />
              ) : (
                <SvgIcon width="13" height="19" IconComponent={ArrowDown} defaultFill={theme.colors.black} />
              )}
            </SummaryFormExtrasStyles.CollapseToggle>
          </SummaryFormExtrasStyles.CollapseTitle>
        </TableCardUI.TableCardRow>

        {isAddonsSectionCollapsed === false && (
          <TableCardUI.TableCardRow className="table-card-row" $depth={3}>
            {renderAddons(
              t,
              effectiveCurrencyCode,
              selectedSupplements,
              selectedFines,
              // @ts-ignore
              availableSupplements,
              availableFines,
              updateSupplementAction,
              updateFineAction,
              id,
              isSr
                ? {
                    ...customItem,
                    actions: customItemActions,
                  }
                : null,
              isConverting ? convert : undefined
            )}
          </TableCardUI.TableCardRow>
        )}
      </TableCardUI.TableCardBox>
    );
  };

  const totalCents = booking?.response?.totals?.totalForPricedItemsCents | 0;

  return (
    <Fragment>
      <TableCardUI.TableCardNumberedBanner className="mt-4 mb-4">
        <TableCardUI.TableCardNumberBannerNumber>3</TableCardUI.TableCardNumberBannerNumber>
        <TableCardUI.TableCardNumberBannerText>Select Your Add-Ons</TableCardUI.TableCardNumberBannerText>
      </TableCardUI.TableCardNumberedBanner>

      {transfers.length >= 1 && renderTransfersWrapper()}
      {groundServices.length >= 1 && renderGroundServicesWrapper()}
      {availableAddons.length >= 1 && renderAddonsWrapper()}

      <TableCardUI.TableCardNumberedBanner className="mt-4 mb-4">
        <TableCardUI.TableCardNumberBannerNumber>4</TableCardUI.TableCardNumberBannerNumber>
        <TableCardUI.TableCardNumberBannerText>Review Costs</TableCardUI.TableCardNumberBannerText>
      </TableCardUI.TableCardNumberedBanner>

      {totalCents > 0 &&
        renderHotelName({
          name: booking?.response?.hotel?.name,
        })}

      {totalCents > 0 && (
        <DisplayTotalsBreakdown
          // @ts-ignore DisplayTotalsBreakdown definitely DOES take a translate func. I think this is styled components being weird
          translate={t}
          currencyCode={effectiveCurrencyCode}
          displayTotals={booking.response.displayTotals}
        />
      )}
      {/* SRs need to be able to select TAs */}
      {hasTAInfo && <TravelAgentInfo />}
    </Fragment>
  );
};

export default compose(connect)(SummaryFormExtras);
